import React, { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useTheme } from "next-themes";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  LayoutDashboard,
  Users,
  FileCheck,
  BookOpen,
  Mail,
  ListChecks,
  Wallet,
  Receipt,
  Calendar,
  BarChart3,
  Bell,
  UserCog,
  Settings,
  Lock,
  Moon,
  Sun,
  LogOut,
  Search,
  ScrollText,
  Bot,
  Menu,
  Landmark,
  Calculator,
  Megaphone,
  ChevronDown,
  Send,
} from "lucide-react";
import alptekinOfisLogo from "@/assets/alptekin-ofis-logo-white.png";
import officeWatermark from "@/assets/office-watermark-dark.png";
import CommandPalette from "@/components/CommandPalette";
import Avatar from "@/components/Avatar";

const NAVY = "#0B2344";
const SIDEBAR_BG = "#081B33";
const GOLD = "#C9952E";

const ROLE_LABELS = {
  admin: "Yönetici",
  mali_musavir: "Mali Müşavir",
  ofis_yoneticisi: "Ofis Yöneticisi",
  kidemli: "Kıdemli",
  muhasebe: "Muhasebe",
};

const NAV = [
  { to: "/", label: "Dashboard", permission: "dashboard", icon: LayoutDashboard, end: true },
  { to: "/mukellefler", label: "Mükellefler", permission: "mukellefler", icon: Users },
  { to: "/beyannameler", label: "Beyannameler", permission: "beyannameler", icon: FileCheck },
  { to: "/tahakkuk-gonderim", label: "Tahakkuk Gönderimi", permission: "beyannameler", icon: Send },
  { to: "/edefter", label: "e-Defter", permission: "edefter", icon: BookOpen },
  { to: "/gib", label: "GİB Entegrasyon", permission: "gib", icon: Landmark },
  { to: "/etebligat", label: "e-Tebligat", permission: "etebligat", icon: Mail },
  { to: "/gorevler", label: "Görevler", permission: "gorevler", icon: ListChecks },
  { to: "/cari", label: "Cari Hesap", permission: "cari", icon: Wallet },
  { to: "/tahsilatlar", label: "Tahsilatlar", permission: "tahsilatlar", icon: Receipt },
  { to: "/takvim", label: "Takvim", permission: "takvim", icon: Calendar },
  { to: "/raporlar", label: "Raporlar", permission: "raporlar", icon: BarChart3 },
  { to: "/araclar", label: "Araçlar", permission: "araclar", icon: Calculator },
  { to: "/mevzuat-takip", label: "Mevzuat Takip", permission: "mevzuat", icon: Megaphone },
  { to: "/bildirimler", label: "Bildirimler", permission: "bildirimler", icon: Bell },
  { to: "/asistan", label: "Akıllı Asistan", permission: "asistan", icon: Bot },
  { to: "/personel", label: "Personel", permission: "personel", icon: UserCog },
  { to: "/audit", label: "İşlem Kayıtları", permission: "audit", icon: ScrollText },
  { to: "/kisisel-alan", label: "Kişisel Alanım", permission: "kisisel", icon: Lock, adminOnly: true },
  { to: "/ayarlar", label: "Ayarlar", permission: "ayarlar", icon: Settings },
];

export default function Layout({ children }) {
  const { theme, setTheme } = useTheme();
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const [cmdOpen, setCmdOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [unsentTah, setUnsentTah] = useState(0);

  /*
   * Admin ve Mali Müşavir tüm bölümleri görür.
   * Diğer kullanıcılar sadece Personel ekranında
   * kendilerine verilen permissions alanlarını görür.
   */
  const hasPermission = (permission) => {
    if (!user) return false;

    if (user.role === "admin" || user.role === "mali_musavir") {
      return true;
    }

    if (!Array.isArray(user.permissions)) {
      return false;
    }

    return user.permissions.includes(permission);
  };

  const visibleNav = NAV.filter((item) =>
    item.adminOnly ? user?.role === "admin" : hasPermission(item.permission)
  );

  useEffect(() => {
    const h = (e) => {
      if (
        (e.metaKey || e.ctrlKey) &&
        e.key.toLowerCase() === "k"
      ) {
        e.preventDefault();
        setCmdOpen(true);
      }
    };

    window.addEventListener("keydown", h);

    return () => window.removeEventListener("keydown", h);
  }, []);

  useEffect(() => {
    if (!hasPermission("bildirimler")) return;
    const loadUnread = () => {
      api.get("/notifications").then((r) => setUnreadCount((r.data || []).filter((n) => !n.read).length)).catch(() => {});
    };
    loadUnread();
    window.addEventListener("notifications:updated", loadUnread);
    return () => window.removeEventListener("notifications:updated", loadUnread);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (!hasPermission("beyannameler")) return;
    const load = () => {
      api.get("/tahakkuk/unsent-count").then((r) => setUnsentTah(r.data?.count || 0)).catch(() => {});
    };
    load();
    window.addEventListener("tahakkuk:updated", load);
    return () => window.removeEventListener("tahakkuk:updated", load);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const todayLabel = new Date().toLocaleDateString("tr-TR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
  const dowLabel = new Date().toLocaleDateString("tr-TR", { weekday: "long" });

  const initials = (user?.name || "?").slice(0, 2).toUpperCase();
  const roleLabel = ROLE_LABELS[user?.role] || user?.role;

  const Sidebar = (
    <aside className="w-64 flex flex-col h-full" style={{ backgroundColor: SIDEBAR_BG }}>

      <div className="flex flex-col items-center justify-center px-3 pt-6 pb-4">
        <img
          src={alptekinOfisLogo}
          alt="Alptekin Ofis"
          className="h-48 w-auto max-w-full object-contain"
        />
      </div>

      <div className="mx-5 h-px bg-white/10 mb-2" />

      <nav className="flex-1 overflow-y-auto py-3 px-3 space-y-0.5">

        {visibleNav.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            onClick={() => setMobileOpen(false)}
            data-testid={`nav-${n.label
              .toLowerCase()
              .replace(/\s|ı/g, "-")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors duration-200 border-l-2 ${
                isActive
                  ? "bg-white/10 text-white font-medium"
                  : "text-white/55 border-transparent hover:bg-white/[0.06] hover:text-white"
              }`
            }
            style={({ isActive }) => ({ borderLeftColor: isActive ? GOLD : "transparent" })}
          >
            <n.icon size={18} strokeWidth={1.8} />
            {n.label}
            {n.to === "/tahakkuk-gonderim" && unsentTah > 0 && (
              <span data-testid="nav-tahakkuk-badge" className="ml-auto text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-500 text-white">{unsentTah}</span>
            )}
          </NavLink>
        ))}

      </nav>

      <div className="p-3 border-t border-white/10">

        <div className="flex items-center gap-2 px-2 py-1.5">

          <Avatar
            user={user}
            className="w-8 h-8 rounded-full grid place-items-center text-xs font-semibold text-white shrink-0"
            style={{ backgroundColor: `${GOLD}33`, border: `1px solid ${GOLD}66` }}
          />

          <div className="flex-1 min-w-0">

            <div className="text-xs font-medium truncate text-white">
              {user?.name}
            </div>

            <div className="text-[10px] text-white/50 truncate">
              {roleLabel}
            </div>

          </div>

          <button
            data-testid="logout-btn"
            onClick={() => {
              logout();
              nav("/login");
            }}
            className="p-1.5 rounded-md hover:bg-white/10 text-white/60 hover:text-white"
          >
            <LogOut size={16} />
          </button>

        </div>

      </div>

    </aside>
  );

  return (
    <div className="flex h-screen overflow-hidden bg-background">

      <div className="hidden lg:block">
        {Sidebar}
      </div>

      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">

          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setMobileOpen(false)}
          />

          <div className="absolute left-0 top-0 h-full">
            {Sidebar}
          </div>

        </div>
      )}

      <div className="flex-1 flex flex-col min-w-0 relative">

        {/* Kurumsal ofis watermark — sadece dekoratif, kaydırılmayan panelin kendisine sabitlenmiş (main'in scroll'undan bağımsız) */}
        <div
          aria-hidden="true"
          className="hidden md:block absolute left-0 right-0 top-16 bottom-0 pointer-events-none select-none z-0 overflow-hidden"
        >
          <img
            src={officeWatermark}
            alt=""
            className="absolute left-0 bottom-0 w-[420px] lg:w-[620px] h-auto max-w-none object-contain opacity-[0.35] lg:opacity-[0.45] dark:opacity-[0.14]"
            style={{
              maskImage: "linear-gradient(105deg, white 0%, white 25%, transparent 60%)",
              WebkitMaskImage: "linear-gradient(105deg, white 0%, white 25%, transparent 60%)",
            }}
          />
        </div>

        <header className="h-16 shrink-0 border-b border-border bg-card/80 backdrop-blur-xl sticky top-0 z-30 px-4 lg:px-6 flex items-center justify-between gap-3">

          <button
            className="lg:hidden p-2 rounded-md hover:bg-accent shrink-0"
            onClick={() => setMobileOpen(true)}
          >
            <Menu size={20} />
          </button>

          <button
            data-testid="open-command-palette"
            onClick={() => setCmdOpen(true)}
            className="flex items-center gap-2 text-sm text-muted-foreground bg-secondary border border-border rounded-lg px-3 py-2 w-full max-w-md hover:bg-accent transition-colors"
          >
            <Search size={16} />

            <span className="flex-1 text-left">
              Ara veya komut...
            </span>

            <kbd className="text-[10px] bg-background border border-border rounded px-1.5 py-0.5">
              ⌘K
            </kbd>

          </button>

          <div className="flex items-center gap-2 shrink-0">

            <div className="hidden md:flex items-center gap-2 text-xs bg-secondary border border-border rounded-lg px-3 py-2 text-muted-foreground">
              <Calendar size={15} />
              <div className="leading-tight">
                <div className="font-medium text-foreground">{todayLabel}</div>
                <div className="capitalize">{dowLabel}</div>
              </div>
            </div>

            {hasPermission("bildirimler") && (
              <button
                data-testid="notif-bell"
                onClick={() => nav("/bildirimler")}
                className="relative p-2 rounded-lg hover:bg-accent transition-colors"
                aria-label="Bildirimler"
              >
                <Bell size={18} />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-semibold grid place-items-center">
                    {unreadCount > 9 ? "9+" : unreadCount}
                  </span>
                )}
              </button>
            )}

            <button
              data-testid="theme-toggle"
              onClick={() =>
                setTheme(theme === "dark" ? "light" : "dark")
              }
              className="p-2 rounded-lg hover:bg-accent transition-colors"
            >
              {theme === "dark" ? (
                <Sun size={18} />
              ) : (
                <Moon size={18} />
              )}
            </button>

            <div className="w-px h-8 bg-border mx-1" />

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="user-menu"
                  className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-lg hover:bg-accent transition-colors"
                >
                  <Avatar
                    user={user}
                    className="w-8 h-8 rounded-full grid place-items-center text-xs font-semibold text-white shrink-0"
                    style={{ backgroundColor: NAVY }}
                  />
                  <div className="hidden sm:block text-left leading-tight">
                    <div className="text-sm font-medium">{user?.name}</div>
                    <div className="text-[11px] text-muted-foreground">{roleLabel}</div>
                  </div>
                  <ChevronDown size={15} className="text-muted-foreground" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {hasPermission("ayarlar") && (
                  <DropdownMenuItem onClick={() => nav("/ayarlar")}>
                    <Settings size={15} className="mr-2" /> Ayarlar
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  data-testid="user-menu-logout"
                  onClick={() => { logout(); nav("/login"); }}
                >
                  <LogOut size={15} className="mr-2" /> Çıkış Yap
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

          </div>

        </header>

        <main className="flex-1 overflow-y-auto p-4 lg:p-8 relative z-10">
          {children}
        </main>

      </div>

      <CommandPalette
        open={cmdOpen}
        setOpen={setCmdOpen}
      />

    </div>
  );
}
